import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { getApiUrl } from '../utils/api';

const Login = ({ onLogin }) => {
    const [isRegistering, setIsRegistering] = useState(false);
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        email: '',
        firstName: '',
        lastName: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const endpoint = isRegistering ? '/api/auth/register' : '/api/auth/login';
            const response = await axios.post(getApiUrl(endpoint), formData);
            
            // Store token and user data
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('user', JSON.stringify(response.data.user));
            
            // Set default authorization header for future requests
            axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
            
            const successMessage = isRegistering ? 'Account created successfully!' : 'Welcome back!';
            toast.success(successMessage);
            onLogin(response.data.user);
        } catch (error) {
            console.error('Auth error:', error);
            const errorMessage = error.response?.data?.error || (isRegistering ? 'Registration failed. Please try again.' : 'Login failed. Please try again.');
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const toggleMode = () => {
        setIsRegistering(!isRegistering);
        setError('');
        setFormData({
            username: '',
            password: '',
            email: '',
            firstName: '',
            lastName: ''
        });
    };

    return (
        <div className="min-vh-100 d-flex align-items-center justify-content-center">
            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="card shadow-lg border-0 fade-in" 
                style={{ maxWidth: '450px', width: '100%' }}
            >
                <div className="card-header text-center py-4">
                    <motion.div
                        initial={{ scale: 0.8 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.3 }}
                    >
                        <img 
                            src="/O_plus_delivery_transparent.png" 
                            alt="Delivery Express" 
                            width="80" 
                            height="80"
                            className="mb-3"
                        />
                        <h2 className="mb-0 fw-bold text-white">
                            <i className="bi bi-rocket me-2"></i>
                            Delivery Express
                        </h2>
                        <p className="mb-0 mt-2 opacity-75 text-white">
                            Enterprise Infrastructure Management
                        </p>
                    </motion.div>
                </div>
                
                <div className="card-body p-4">
                    {error && (
                        <motion.div 
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="alert alert-danger" 
                            role="alert"
                        >
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            {error}
                        </motion.div>
                    )}
                    
                    <form onSubmit={handleSubmit}>
                        {isRegistering && (
                            <>
                                <motion.div 
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.1 }}
                                    className="mb-3"
                                >
                                    <label htmlFor="firstName" className="form-label">
                                        <i className="bi bi-person me-2"></i>
                                        First Name
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="firstName"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        placeholder="Enter your first name"
                                        required={isRegistering}
                                        disabled={loading}
                                    />
                                </motion.div>
                                
                                <motion.div 
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.2 }}
                                    className="mb-3"
                                >
                                    <label htmlFor="lastName" className="form-label">
                                        <i className="bi bi-person me-2"></i>
                                        Last Name
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="lastName"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        placeholder="Enter your last name"
                                        required={isRegistering}
                                        disabled={loading}
                                    />
                                </motion.div>
                                
                                <motion.div 
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.3 }}
                                    className="mb-3"
                                >
                                    <label htmlFor="email" className="form-label">
                                        <i className="bi bi-envelope me-2"></i>
                                        Email Address
                                    </label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="Enter your email address"
                                        required={isRegistering}
                                        disabled={loading}
                                    />
                                </motion.div>
                            </>
                        )}
                        
                        <motion.div 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: isRegistering ? 0.4 : 0.1 }}
                            className="mb-3"
                        >
                            <label htmlFor="username" className="form-label">
                                <i className="bi bi-person me-2"></i>
                                Username
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                id="username"
                                name="username"
                                value={formData.username}
                                onChange={handleChange}
                                placeholder="Enter your username"
                                required
                                disabled={loading}
                            />
                        </motion.div>
                        
                        <motion.div 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: isRegistering ? 0.5 : 0.2 }}
                            className="mb-4"
                        >
                            <label htmlFor="password" className="form-label">
                                <i className="bi bi-lock me-2"></i>
                                Password
                            </label>
                            <input
                                type="password"
                                className="form-control"
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                placeholder="Enter your password"
                                required
                                disabled={loading}
                            />
                        </motion.div>
                        
                        <motion.button
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: isRegistering ? 0.6 : 0.3 }}
                            type="submit"
                            className="btn btn-primary w-100 d-flex align-items-center justify-content-center"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <div className="loading-spinner me-2"></div>
                                    {isRegistering ? 'Creating account...' : 'Signing in...'}
                                </>
                            ) : (
                                <>
                                    <i className={`bi bi-${isRegistering ? 'person-plus' : 'box-arrow-in-right'} me-2`}></i>
                                    {isRegistering ? 'Create Account' : 'Sign In'}
                                </>
                            )}
                        </motion.button>
                    </form>
                    
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.7 }}
                        className="mt-4 text-center"
                    >
                        <button 
                            type="button" 
                            className="btn btn-link text-decoration-none"
                            onClick={toggleMode}
                        >
                            {isRegistering ? 'Already have an account? Sign in' : "Don't have an account? Register"}
                        </button>
                    </motion.div>

                    {!isRegistering && (
                        <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.8 }}
                            className="mt-4 text-center"
                        >

                        </motion.div>
                    )}

                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.9 }}
                        className="mt-4 p-3 bg-light rounded"
                    >
                        <h6 className="mb-2 fw-bold">
                            <i className="bi bi-shield-check me-2 text-primary"></i>
                            Enterprise Features
                        </h6>
                        <div className="row text-center">
                            <div className="col-4">
                                <i className="bi bi-people-fill text-primary mb-1"></i>
                                <div className="small">Team Access</div>
                            </div>
                            <div className="col-4">
                                <i className="bi bi-graph-up text-success mb-1"></i>
                                <div className="small">Analytics</div>
                            </div>
                            <div className="col-4">
                                <i className="bi bi-gear-fill text-warning mb-1"></i>
                                <div className="small">Management</div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </motion.div>
        </div>
    );
};

export default Login; 